import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import type { Job } from "@/pages/admin/jobs/components/JobModal";

/* ─────────────────────────────── Types ─────────────────────────────── */
type EventRoute = {
  id: string;
  tenant_id: string;
  job_id: string;
  client_id: string | null;
  title: string;
  event_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

type RouteStop = {
  id: string;
  tenant_id: string;
  route_id: string;
  label: string;
  address: string;
  place_type: string;
  time_scheduled: string | null;
  duration_min: number | null;
  notes: string | null;
  sort_order: number;
  created_at: string;
};

/* ─────────────────────────── Place type config ──────────────────────── */
type PlaceType = {
  value: string;
  label: string;
  icon: string;
  color: string;
  bg: string;
  border: string;
};

const PLACE_TYPES: PlaceType[] = [
  { value: "home",        label: "Casa / Home",       icon: "ri-home-4-line",          color: "text-amber-600",   bg: "bg-amber-50",   border: "border-amber-200" },
  { value: "session",     label: "Sesión / Session",   icon: "ri-camera-3-line",         color: "text-sky-600",     bg: "bg-sky-50",     border: "border-sky-200" },
  { value: "church",      label: "Iglesia / Church",   icon: "ri-building-4-line",       color: "text-violet-600",  bg: "bg-violet-50",  border: "border-violet-200" },
  { value: "venue",       label: "Salón / Venue",      icon: "ri-goblet-line",           color: "text-[#a6853e]",   bg: "bg-[#C8A96E]/10", border: "border-[#C8A96E]/40" },
  { value: "garden",      label: "Jardín / Garden",    icon: "ri-plant-line",            color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  { value: "beach",       label: "Playa / Beach",      icon: "ri-landscape-line",        color: "text-cyan-600",    bg: "bg-cyan-50",    border: "border-cyan-200" },
  { value: "hotel",       label: "Hotel",              icon: "ri-hotel-line",            color: "text-rose-600",    bg: "bg-rose-50",    border: "border-rose-200" },
  { value: "restaurant",  label: "Restaurante",        icon: "ri-restaurant-line",       color: "text-orange-600",  bg: "bg-orange-50",  border: "border-orange-200" },
  { value: "studio",      label: "Estudio / Studio",   icon: "ri-ancient-gate-line",     color: "text-slate-600",   bg: "bg-slate-100",  border: "border-slate-300" },
  { value: "park",        label: "Parque / Park",      icon: "ri-map-pin-2-line",        color: "text-lime-600",    bg: "bg-lime-50",    border: "border-lime-200" },
  { value: "transport",   label: "Transporte",         icon: "ri-car-line",              color: "text-slate-500",   bg: "bg-slate-100",  border: "border-slate-200" },
  { value: "other",       label: "Otro / Other",       icon: "ri-map-pin-line",          color: "text-slate-500",   bg: "bg-slate-50",   border: "border-slate-200" },
];

function getPlaceType(value: string): PlaceType {
  return PLACE_TYPES.find((p) => p.value === value) ?? PLACE_TYPES[PLACE_TYPES.length - 1];
}

/* ──────────────────────────── Helpers ──────────────────────────────── */
function buildGoogleMapsUrl(stops: RouteStop[]): string {
  const addrs = stops.map((s) => encodeURIComponent(s.address));
  if (addrs.length === 0) return "https://maps.google.com";
  if (addrs.length === 1) return `https://www.google.com/maps/search/?api=1&query=${addrs[0]}`;
  const origin = addrs[0];
  const destination = addrs[addrs.length - 1];
  const waypoints = addrs.slice(1, -1).join("|");
  return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints ? `&waypoints=${waypoints}` : ""}`;
}

function buildWazeUrl(stop: RouteStop): string {
  return `https://waze.com/ul?q=${encodeURIComponent(stop.address)}&navigate=yes`;
}

function fmtDate(d: string | null) {
  if (!d) return "";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

/* ═══════════════════════════ MAIN COMPONENT ═════════════════════════ */
interface Props {
  job: Job;
  tenantId: string;
}

type RouteForm = { title: string; event_date: string; notes: string };
const EMPTY_ROUTE_FORM: RouteForm = { title: "Event Route", event_date: "", notes: "" };

type StopForm = {
  label: string;
  address: string;
  place_type: string;
  time_scheduled: string;
  duration_min: string;
  notes: string;
};
const EMPTY_STOP: StopForm = { label: "", address: "", place_type: "other", time_scheduled: "", duration_min: "", notes: "" };

export default function EventRoutesTab({ job, tenantId }: Props) {
  const [routes, setRoutes] = useState<EventRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeRouteId, setActiveRouteId] = useState<string | null>(null);
  const [stops, setStops] = useState<RouteStop[]>([]);
  const [stopsLoading, setStopsLoading] = useState(false);

  // Route form
  const [showRouteForm, setShowRouteForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState<EventRoute | null>(null);
  const [routeForm, setRouteForm] = useState<RouteForm>({ ...EMPTY_ROUTE_FORM });
  const [savingRoute, setSavingRoute] = useState(false);

  // Stop form
  const [showStopForm, setShowStopForm] = useState(false);
  const [editingStop, setEditingStop] = useState<RouteStop | null>(null);
  const [stopForm, setStopForm] = useState<StopForm>({ ...EMPTY_STOP });
  const [savingStop, setSavingStop] = useState(false);


  /* ── Fetch routes ── */
  const fetchRoutes = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("event_routes")
      .select("*")
      .eq("job_id", job.id)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: true });
    const list = (data ?? []) as EventRoute[];
    setRoutes(list);
    if (list.length > 0 && !activeRouteId) {
      setActiveRouteId(list[0].id);
    }
    setLoading(false);
  }, [job.id, tenantId, activeRouteId]);

  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  /* ── Fetch stops for active route ── */
  const fetchStops = useCallback(async (routeId: string) => {
    setStopsLoading(true);
    const { data } = await supabase
      .from("event_route_stops")
      .select("*")
      .eq("route_id", routeId)
      .eq("tenant_id", tenantId)
      .order("sort_order", { ascending: true });
    setStops((data ?? []) as RouteStop[]);
    setStopsLoading(false);
  }, [tenantId]);

  useEffect(() => {
    if (activeRouteId) fetchStops(activeRouteId);
  }, [activeRouteId, fetchStops]);

  const activeRoute = routes.find((r) => r.id === activeRouteId) ?? null;

  /* ── Route CRUD ── */
  const openNewRoute = () => {
    setEditingRoute(null);
    setRouteForm({
      title: `${job.job_type} Route`,
      event_date: job.shoot_date ? job.shoot_date.slice(0, 10) : "",
      notes: "",
    });
    setShowRouteForm(true);
  };

  const openEditRoute = (r: EventRoute) => {
    setEditingRoute(r);
    setRouteForm({ title: r.title, event_date: r.event_date ?? "", notes: r.notes ?? "" });
    setShowRouteForm(true);
  };

  const cancelRouteForm = () => { setShowRouteForm(false); setEditingRoute(null); };

  const handleSaveRoute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!routeForm.title.trim()) return;
    setSavingRoute(true);
    const payload = {
      title: routeForm.title.trim(),
      event_date: routeForm.event_date || null,
      notes: routeForm.notes.trim() || null,
      client_id: job.client_id || null,
    };
    if (editingRoute) {
      const { data } = await supabase
        .from("event_routes")
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq("id", editingRoute.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();
      if (data) setRoutes((prev) => prev.map((r) => r.id === editingRoute.id ? (data as EventRoute) : r));
    } else {
      const { data } = await supabase
        .from("event_routes")
        .insert({ ...payload, tenant_id: tenantId, job_id: job.id })
        .select()
        .maybeSingle();
      if (data) {
        setRoutes((prev) => [...prev, data as EventRoute]);
        setActiveRouteId((data as EventRoute).id);
      }
    }
    setSavingRoute(false);
    cancelRouteForm();
  };

  const handleDeleteRoute = async (r: EventRoute) => {
    const result = await Swal.fire({
      title: "Delete route?",
      text: `"${r.title}" and all its stops will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      background: "#fff",
      color: "#1e293b",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;
    await supabase.from("event_routes").delete().eq("id", r.id).eq("tenant_id", tenantId);
    const updated = routes.filter((x) => x.id !== r.id);
    setRoutes(updated);
    if (activeRouteId === r.id) {
      setActiveRouteId(updated.length > 0 ? updated[0].id : null);
      setStops([]);
    }
  };

  /* ── Stop CRUD ── */
  const openNewStop = () => {
    setEditingStop(null);
    setStopForm({ ...EMPTY_STOP });
    setShowStopForm(true);
  };

  const openEditStop = (s: RouteStop) => {
    setEditingStop(s);
    setStopForm({
      label: s.label,
      address: s.address,
      place_type: s.place_type,
      time_scheduled: s.time_scheduled ?? "",
      duration_min: s.duration_min != null ? String(s.duration_min) : "",
      notes: s.notes ?? "",
    });
    setShowStopForm(true);
  };

  const cancelStopForm = () => { setShowStopForm(false); setEditingStop(null); };

  const handleSaveStop = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stopForm.label.trim() || !stopForm.address.trim() || !activeRouteId) return;
    setSavingStop(true);
    const payload = {
      label: stopForm.label.trim(),
      address: stopForm.address.trim(),
      place_type: stopForm.place_type,
      time_scheduled: stopForm.time_scheduled || null,
      duration_min: stopForm.duration_min ? parseInt(stopForm.duration_min, 10) : null,
      notes: stopForm.notes.trim() || null,
    };
    if (editingStop) {
      const { data } = await supabase
        .from("event_route_stops")
        .update(payload)
        .eq("id", editingStop.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();
      if (data) setStops((prev) => prev.map((s) => s.id === editingStop.id ? (data as RouteStop) : s));
    } else {
      const { data } = await supabase
        .from("event_route_stops")
        .insert({ ...payload, tenant_id: tenantId, route_id: activeRouteId, sort_order: stops.length })
        .select()
        .maybeSingle();
      if (data) setStops((prev) => [...prev, data as RouteStop]);
    }
    setSavingStop(false);
    cancelStopForm();
  };

  const handleDeleteStop = async (s: RouteStop) => {
    const result = await Swal.fire({
      title: "Remove stop?",
      text: `"${s.label}" will be removed from this route.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      background: "#fff",
      color: "#1e293b",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;
    await supabase.from("event_route_stops").delete().eq("id", s.id).eq("tenant_id", tenantId);
    setStops((prev) => prev.filter((x) => x.id !== s.id));
  };

  /* ── Reorder stops ── */
  const moveStop = async (index: number, direction: "up" | "down") => {
    const newStops = [...stops];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newStops.length) return;
    [newStops[index], newStops[targetIndex]] = [newStops[targetIndex], newStops[index]];
    const updated = newStops.map((s, i) => ({ ...s, sort_order: i }));
    setStops(updated);
    // Persist order
    await Promise.all(
      updated.map((s) =>
        supabase.from("event_route_stops")
          .update({ sort_order: s.sort_order })
          .eq("id", s.id)
          .eq("tenant_id", tenantId)
      )
    );
  };

  /* ── Total time calc ── */
  const totalMinutes = stops.reduce((acc, s) => acc + (s.duration_min ?? 0), 0);
  const totalHrs = Math.floor(totalMinutes / 60);
  const totalMins = totalMinutes % 60;

  /* ══════════════════════════════ RENDER ═══════════════════════════════ */
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* ── Route selector header ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Route tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          {routes.map((r) => (
            <button
              key={r.id}
              onClick={() => setActiveRouteId(r.id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                activeRouteId === r.id
                  ? "bg-[#C8A96E]/10 border-[#C8A96E]/40 text-[#a6853e]"
                  : "bg-white border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-route-line text-xs"></i>
              </span>
              {r.title}
            </button>
          ))}
          {!showRouteForm && (
            <button
              onClick={openNewRoute}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-[#C8A96E] border border-dashed border-slate-200 hover:border-[#C8A96E]/40 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-xs"></i></span>
              New Route
            </button>
          )}
        </div>

        {/* Actions for active route */}
        {activeRoute && !showStopForm && !showRouteForm && (
          <div className="flex items-center gap-2">
            {stops.length > 0 && (
              <>
                <a
                  href={buildGoogleMapsUrl(stops)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-500 hover:text-emerald-700 transition-all cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-2-line text-xs"></i></span>
                  Google Maps
                </a>
              </>
            )}
            <button
              onClick={openNewStop}
              className="flex items-center gap-1.5 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold px-4 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm"></i></span>
              Add Stop
            </button>
          </div>
        )}
      </div>

      {/* ── Route form ── */}
      {showRouteForm && (
        <form onSubmit={handleSaveRoute} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
              <i className="ri-route-line text-[#C8A96E]"></i>
              {editingRoute ? "Edit Route" : "New Event Route"}
            </p>
            <button type="button" onClick={cancelRouteForm} className="text-slate-300 hover:text-slate-500 cursor-pointer transition-colors">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-close-line"></i></span>
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Route Name *</label>
              <input
                type="text" value={routeForm.title}
                onChange={(e) => setRouteForm((p) => ({ ...p, title: e.target.value }))}
                required placeholder="Wedding Day Route"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>
            <div>
              <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Event Date</label>
              <input
                type="date" value={routeForm.event_date}
                onChange={(e) => setRouteForm((p) => ({ ...p, event_date: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Notes</label>
              <input
                type="text" value={routeForm.notes}
                onChange={(e) => setRouteForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Meeting point, dress code, special instructions..."
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cancelRouteForm} className="px-4 py-2 text-sm text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer whitespace-nowrap">Cancel</button>
            <button type="submit" disabled={savingRoute} className="flex items-center gap-1.5 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
              {savingRoute ? <><i className="ri-loader-4-line animate-spin"></i> Saving…</> : "Save Route"}
            </button>
          </div>
        </form>
      )}

      {/* ── No routes empty state ── */}
      {routes.length === 0 && !showRouteForm && (
        <div className="flex flex-col items-center gap-4 py-20 bg-white rounded-xl border border-dashed border-slate-200">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-slate-100">
            <i className="ri-route-line text-slate-300 text-3xl"></i>
          </div>
          <div className="text-center">
            <p className="text-[#0f172a] font-semibold mb-1">No event routes yet</p>
            <p className="text-slate-400 text-sm max-w-xs">Plan the full day — stops, addresses and timelines for this event.</p>
          </div>
          <button
            onClick={openNewRoute}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Create First Route
          </button>
        </div>
      )}

      {/* ── Active route view ── */}
      {activeRoute && !showRouteForm && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* LEFT: Timeline / stops */}
          <div className="lg:col-span-2 space-y-4">

            {/* Route header card */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C8A96E]/10 flex-shrink-0">
                    <i className="ri-route-line text-[#C8A96E] text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-[#0f172a] font-bold text-base">{activeRoute.title}</h3>
                    {activeRoute.event_date && (
                      <p className="text-slate-400 text-xs mt-0.5 flex items-center gap-1.5">
                        <i className="ri-calendar-event-line text-[#C8A96E]"></i>
                        {fmtDate(activeRoute.event_date)}
                      </p>
                    )}
                    {activeRoute.notes && (
                      <p className="text-slate-500 text-xs mt-1">{activeRoute.notes}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => openEditRoute(activeRoute)} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-all cursor-pointer">
                    <i className="ri-pencil-line text-sm"></i>
                  </button>
                  <button onClick={() => handleDeleteRoute(activeRoute)} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all cursor-pointer">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>

              {/* Stats bar */}
              {stops.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-5 flex-wrap">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <i className="ri-map-pin-2-line text-[#C8A96E]"></i>
                    <span className="font-semibold text-[#0f172a]">{stops.length}</span> stops
                  </div>
                  {totalMinutes > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <i className="ri-time-line text-[#C8A96E]"></i>
                      <span className="font-semibold text-[#0f172a]">
                        {totalHrs > 0 ? `${totalHrs}h ` : ""}{totalMins > 0 ? `${totalMins}min` : ""}
                      </span> total duration
                    </div>
                  )}
                  <div className="ml-auto flex items-center gap-2">
                    <a
                      href={buildGoogleMapsUrl(stops)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-map-2-line text-xs"></i>
                      Full Route → Maps
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Stop form */}
            {showStopForm && (
              <form onSubmit={handleSaveStop} className="bg-white rounded-xl border border-[#C8A96E]/30 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
                    <i className="ri-map-pin-add-line text-[#C8A96E]"></i>
                    {editingStop ? "Edit Stop" : "New Stop"}
                  </p>
                  <button type="button" onClick={cancelStopForm} className="text-slate-300 hover:text-slate-500 cursor-pointer">
                    <span className="w-5 h-5 flex items-center justify-center"><i className="ri-close-line"></i></span>
                  </button>
                </div>

                {/* Place type selector */}
                <div>
                  <label className="text-slate-400 text-xs mb-2 block uppercase tracking-wider">Place Type</label>
                  <div className="flex flex-wrap gap-2">
                    {PLACE_TYPES.map((pt) => (
                      <button
                        key={pt.value}
                        type="button"
                        onClick={() => setStopForm((p) => ({ ...p, place_type: pt.value }))}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                          stopForm.place_type === pt.value
                            ? `${pt.bg} ${pt.border} ${pt.color}`
                            : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                        }`}
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={pt.icon}></i></span>
                        {pt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Stop Label *</label>
                    <input
                      type="text" value={stopForm.label}
                      onChange={(e) => setStopForm((p) => ({ ...p, label: e.target.value }))}
                      required placeholder="Iglesia San José"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50"
                    />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Scheduled Time</label>
                    <input
                      type="time" value={stopForm.time_scheduled}
                      onChange={(e) => setStopForm((p) => ({ ...p, time_scheduled: e.target.value }))}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 cursor-pointer"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Address *</label>
                    <input
                      type="text" value={stopForm.address}
                      onChange={(e) => setStopForm((p) => ({ ...p, address: e.target.value }))}
                      required placeholder="Calle Principal 123, Ciudad"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50"
                    />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Duration (min)</label>
                    <input
                      type="number" value={stopForm.duration_min} min="0"
                      onChange={(e) => setStopForm((p) => ({ ...p, duration_min: e.target.value }))}
                      placeholder="60"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50"
                    />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Notes</label>
                    <input
                      type="text" value={stopForm.notes}
                      onChange={(e) => setStopForm((p) => ({ ...p, notes: e.target.value }))}
                      placeholder="Parking info, contact..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button type="button" onClick={cancelStopForm} className="px-4 py-2 text-sm text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer whitespace-nowrap">Cancel</button>
                  <button type="submit" disabled={savingStop} className="flex items-center gap-1.5 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                    {savingStop ? <><i className="ri-loader-4-line animate-spin"></i> Saving…</> : (editingStop ? "Update Stop" : "Add Stop")}
                  </button>
                </div>
              </form>
            )}

            {/* Timeline */}
            {stopsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}
              </div>
            ) : stops.length === 0 && !showStopForm ? (
              <div className="flex flex-col items-center gap-3 py-14 bg-white rounded-xl border border-dashed border-slate-200 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-100">
                  <i className="ri-map-pin-line text-slate-300 text-xl"></i>
                </div>
                <p className="text-slate-500 text-sm font-medium">No stops yet</p>
                <p className="text-slate-400 text-xs max-w-xs">Add locations like home, church, venue, garden session, etc.</p>
                <button onClick={openNewStop} className="text-xs text-[#C8A96E] hover:text-[#a6853e] cursor-pointer transition-colors flex items-center gap-1">
                  <i className="ri-add-line text-xs"></i>
                  Add first stop
                </button>
              </div>
            ) : (
              <div className="space-y-0">
                {stops.map((stop, idx) => {
                  const pt = getPlaceType(stop.place_type);
                  const isLast = idx === stops.length - 1;
                  return (
                    <div key={stop.id} className="flex gap-4">
                      {/* Timeline line */}
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${pt.bg} border-2 ${pt.border} flex-shrink-0 relative z-10`}>
                          <i className={`${pt.icon} ${pt.color} text-base`}></i>
                        </div>
                        {!isLast && (
                          <div className="w-0.5 flex-1 bg-slate-200 my-1" style={{ minHeight: "24px" }}></div>
                        )}
                      </div>

                      {/* Stop card */}
                      <div className={`flex-1 bg-white rounded-xl border border-slate-200 p-4 group transition-all hover:border-slate-300 ${isLast ? "mb-0" : "mb-0"}`}
                        style={{ marginBottom: isLast ? 0 : "8px" }}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              {stop.time_scheduled && (
                                <span className="text-[#C8A96E] text-xs font-bold font-mono bg-[#C8A96E]/10 px-2 py-0.5 rounded-md whitespace-nowrap">
                                  {stop.time_scheduled}
                                </span>
                              )}
                              <p className="text-[#0f172a] font-semibold text-sm">{stop.label}</p>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${pt.bg} ${pt.color} border ${pt.border}`}>
                                {pt.label}
                              </span>
                            </div>
                            <p className="text-slate-500 text-xs flex items-start gap-1.5 mb-1">
                              <i className="ri-map-pin-line text-slate-300 text-xs mt-0.5 flex-shrink-0"></i>
                              {stop.address}
                            </p>
                            <div className="flex items-center gap-3 flex-wrap">
                              {stop.duration_min && (
                                <span className="text-slate-400 text-xs flex items-center gap-1">
                                  <i className="ri-time-line text-xs text-slate-300"></i>
                                  {stop.duration_min >= 60
                                    ? `${Math.floor(stop.duration_min / 60)}h${stop.duration_min % 60 > 0 ? ` ${stop.duration_min % 60}min` : ""}`
                                    : `${stop.duration_min} min`}
                                </span>
                              )}
                              {stop.notes && (
                                <span className="text-slate-400 text-xs flex items-center gap-1">
                                  <i className="ri-information-line text-xs text-slate-300"></i>
                                  {stop.notes}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => moveStop(idx, "up")}
                              disabled={idx === 0}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-all cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed"
                              title="Move up"
                            >
                              <i className="ri-arrow-up-s-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => moveStop(idx, "down")}
                              disabled={idx === stops.length - 1}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-all cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed"
                              title="Move down"
                            >
                              <i className="ri-arrow-down-s-line text-sm"></i>
                            </button>
                            {/* Navigation buttons */}
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stop.address)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all cursor-pointer"
                              title="Open in Google Maps"
                            >
                              <i className="ri-map-2-line text-sm"></i>
                            </a>
                            <a
                              href={buildWazeUrl(stop)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-sky-600 hover:bg-sky-50 transition-all cursor-pointer"
                              title="Navigate in Waze"
                            >
                              <i className="ri-navigation-line text-sm"></i>
                            </a>
                            <button
                              onClick={() => openEditStop(stop)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-all cursor-pointer"
                              title="Edit stop"
                            >
                              <i className="ri-pencil-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteStop(stop)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all cursor-pointer"
                              title="Remove stop"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Add stop inline button */}
                {!showStopForm && (
                  <div className="flex gap-4 mt-2">
                    <div className="w-10 flex justify-center">
                      <div className="w-0.5 h-6 bg-slate-200"></div>
                    </div>
                    <button
                      onClick={openNewStop}
                      className="flex items-center gap-2 text-xs text-slate-400 hover:text-[#C8A96E] border border-dashed border-slate-200 hover:border-[#C8A96E]/40 rounded-lg px-4 py-2 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line text-xs"></i>
                      Add another stop
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: Summary panel */}
          <div className="space-y-4">

            {/* Quick nav card */}
            {stops.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
                <h3 className="text-slate-400 text-xs uppercase tracking-wider flex items-center gap-2">
                  <i className="ri-navigation-line text-[#C8A96E] text-xs"></i>
                  Navigate
                </h3>
                <a
                  href={buildGoogleMapsUrl(stops)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 w-full p-3 rounded-lg bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-all cursor-pointer group"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500 flex-shrink-0">
                    <i className="ri-map-2-line text-white text-base"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-emerald-700 font-semibold text-sm">Google Maps</p>
                    <p className="text-emerald-600 text-xs">Full route with {stops.length} stops</p>
                  </div>
                  <i className="ri-external-link-line text-emerald-400 text-sm group-hover:text-emerald-600 transition-colors"></i>
                </a>
                {stops.slice(0, 4).map((stop) => (
                  <a
                    key={stop.id}
                    href={buildWazeUrl(stop)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 w-full p-3 rounded-lg bg-sky-50 border border-sky-200 hover:bg-sky-100 transition-all cursor-pointer group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-sky-500 flex-shrink-0">
                      <i className="ri-navigation-line text-white text-base"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sky-700 font-semibold text-sm truncate">Waze → {stop.label}</p>
                      <p className="text-sky-600 text-xs truncate">{stop.address}</p>
                    </div>
                    <i className="ri-external-link-line text-sky-400 text-sm group-hover:text-sky-600 transition-colors"></i>
                  </a>
                ))}
              </div>
            )}

            {/* Timeline summary */}
            {stops.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                  <i className="ri-time-line text-[#C8A96E] text-xs"></i>
                  Day Timeline
                </h3>
                <div className="space-y-2">
                  {stops.map((stop, idx) => {
                    const pt = getPlaceType(stop.place_type);
                    return (
                      <div key={stop.id} className="flex items-center gap-3">
                        <div className={`w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0 ${pt.bg} border ${pt.border}`}>
                          <i className={`${pt.icon} text-[10px] ${pt.color}`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-700 text-xs font-medium truncate">{stop.label}</p>
                          {stop.duration_min && (
                            <p className="text-slate-300 text-[10px]">{stop.duration_min} min</p>
                          )}
                        </div>
                        <span className={`text-[10px] font-mono font-bold flex-shrink-0 ${stop.time_scheduled ? "text-[#C8A96E]" : "text-slate-200"}`}>
                          {stop.time_scheduled || `#${idx + 1}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {totalMinutes > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                    <span className="text-slate-400">Total duration</span>
                    <span className="font-semibold text-[#0f172a]">
                      {totalHrs > 0 ? `${totalHrs}h ` : ""}{totalMins > 0 ? `${totalMins}min` : ""}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Stops count by type */}
            {stops.length > 1 && (() => {
              const byType: Record<string, number> = {};
              stops.forEach((s) => { byType[s.place_type] = (byType[s.place_type] ?? 0) + 1; });
              return (
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                    <i className="ri-bar-chart-grouped-line text-[#C8A96E] text-xs"></i>
                    Stop Types
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(byType).map(([type, count]) => {
                      const pt = getPlaceType(type);
                      return (
                        <div key={type} className="flex items-center gap-2">
                          <div className={`w-6 h-6 flex items-center justify-center rounded-lg ${pt.bg} border ${pt.border} flex-shrink-0`}>
                            <i className={`${pt.icon} text-[10px] ${pt.color}`}></i>
                          </div>
                          <span className="text-slate-600 text-xs flex-1">{pt.label}</span>
                          <span className="text-slate-400 text-xs font-semibold">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

